import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Baby, ChevronRight, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import type { StageParticipation } from '../../types/trophy';

interface EntryIdentityDialogProps {
  /** The stage being entered; null keeps the dialog closed. */
  stage: StageParticipation | null;
  onCancel: () => void;
  onChoose: (who: 'self' | 'ward') => void;
}

/**
 * The first question a visitor with no account is asked: who is climbing?
 *
 * It exists because the two answers need different accounts. "Myself" is an
 * ordinary sign-up. "My child" is not: under 14 a child cannot have an account
 * of their own, so what has to be created is a parent's account holding one
 * for them — a different form, asked in a different order (the athletes first,
 * because that is what the parent came to do). Guessing wrong and asking for a
 * username and a password for a nine-year-old is the mistake this prevents.
 *
 * Signing in is on the same screen: somebody who already has an account and
 * tapped "enter" is here by accident.
 */
export default function EntryIdentityDialog({ stage, onCancel, onChoose }: EntryIdentityDialogProps) {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <Dialog open={!!stage} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] p-4 sm:max-w-md sm:p-6">
        <DialogHeader>
          <DialogTitle>{t('stageEntry.identity.title', { stage: stage?.name ?? '' })}</DialogTitle>
          <DialogDescription>{t('stageEntry.identity.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <button
            type="button"
            onClick={() => onChoose('self')}
            data-testid="enter-as-self"
            className="flex w-full items-center gap-3 rounded-lg border p-4 text-left transition hover:bg-accent"
          >
            <User className="size-5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="flex-1">
              <span className="block font-medium">{t('stageEntry.identity.self')}</span>
              <span className="block text-xs text-muted-foreground">
                {t('stageEntry.identity.selfHint')}
              </span>
            </span>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          </button>

          <button
            type="button"
            onClick={() => onChoose('ward')}
            data-testid="enter-as-guardian"
            className="flex w-full items-center gap-3 rounded-lg border p-4 text-left transition hover:bg-accent"
          >
            <Baby className="size-5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="flex-1">
              <span className="block font-medium">{t('stageEntry.identity.ward')}</span>
              <span className="block text-xs text-muted-foreground">
                {t('stageEntry.identity.wardHint')}
              </span>
            </span>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          </button>
        </div>

        <DialogFooter className="sm:justify-start">
          <p className="text-sm text-muted-foreground">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link
              to="/sign-in"
              state={{ from: `${location.pathname}${location.search}` }}
              className="text-primary hover:underline"
            >
              {t('auth.signInNow')}
            </Link>
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
